import { useState, useCallback, useEffect } from 'react';
import { Loader2, RefreshCw, Brain, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { reconciliationMemoryAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const TARGET_KIND_LABELS = {
  recurring_cost: 'Recurring cost',
  supplier: 'Supplier',
  statement: 'Statement',
};

// Learned counterparty -> target priors (see services/reconciliation_memory.py).
// Visible tally of memory-driven confirms + per-row Unlearn action -- a
// wrong learned pattern must be killable from the UI, not Atlas.
export function ReconciliationMemoryCard() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, rowsRes] = await Promise.all([
        reconciliationMemoryAPI.getStats(),
        reconciliationMemoryAPI.getAll(),
      ]);
      setStats(statsRes.data);
      setRows(rowsRes.data || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUnlearn = async (row) => {
    try {
      await reconciliationMemoryAPI.unlearn(row.id);
      toast.success(`Unlearned "${row.fingerprint}" -> ${row.target_id}`);
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Card data-testid="reconciliation-memory-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-4 w-4" />
          Reconciliation Memory
        </CardTitle>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {stats && (
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div className="rounded-md p-2 bg-slate-50 dark:bg-slate-900/30">
              <div className="text-xs text-muted-foreground">Learned patterns</div>
              <div className="text-lg font-mono">{stats.total_rows}</div>
            </div>
            <div className="rounded-md p-2 bg-green-50 dark:bg-green-900/20">
              <div className="text-xs text-muted-foreground">Auto-confirm eligible</div>
              <div className="text-lg font-mono">{stats.auto_confirm_eligible_rows}</div>
            </div>
            <div className="rounded-md p-2 bg-slate-50 dark:bg-slate-900/30">
              <div className="text-xs text-muted-foreground">Auto-confirm</div>
              <div className="text-sm font-medium mt-1">
                {stats.auto_confirm_enabled ? (
                  <span className="text-green-600 dark:text-green-400">Enabled</span>
                ) : (
                  <span className="text-amber-600 dark:text-amber-400">Disabled (suggest-only)</span>
                )}
              </div>
            </div>
          </div>
        )}

        {rows.length > 0 && (
          <div>
            <button
              type="button"
              className="text-xs text-muted-foreground underline"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? 'Hide' : 'Show'} {rows.length} learned pattern(s)
            </button>
            {expanded && (
              <div className="overflow-x-auto mt-2">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      <th className="py-1 pr-2">Fingerprint</th>
                      <th className="py-1 pr-2">Target</th>
                      <th className="py-1 pr-2 text-right">Confirms</th>
                      <th className="py-1 pr-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id} className="border-t">
                        <td className="py-1 pr-2 font-mono">{row.fingerprint}</td>
                        <td className="py-1 pr-2">
                          <Badge variant="outline" className="text-[10px] px-1 py-0 mr-1">
                            {TARGET_KIND_LABELS[row.target_kind] || row.target_kind}
                          </Badge>
                          {row.target_id}
                        </td>
                        <td className="py-1 pr-2 text-right font-mono">
                          {row.confirm_count}
                          {row.confirm_count >= 2 && (
                            <Badge className="ml-1 text-[9px] px-1 py-0 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                              auto
                            </Badge>
                          )}
                        </td>
                        <td className="py-1 pr-2 text-right">
                          <Button
                            size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-destructive"
                            onClick={() => handleUnlearn(row)}
                            data-testid={`unlearn-${row.id}`}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Unlearn
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
